import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuMail,
  LuPlus,
  LuSend,
  LuUpload,
  LuTrash2,
  LuPause,
  LuPlay,
  LuCheck,
  LuUsers,
  LuEye,
  LuLink,
  LuUnlink,
  LuPaperclip,
  LuRefreshCw,
  LuList,
  LuDownload,
} from "react-icons/lu";
import { Btn, Badge, Field, Spinner, Skel, Note, Dialog } from "../components/Ui";
import "./Outreach.css";

const OAUTH_API = "https://us-east1-foursssolutions.cloudfunctions.net/email_outreach_oauth_v2";
const API = "https://us-east1-foursssolutions.cloudfunctions.net/email_outreach_api_v2";
const SEND_API = "https://us-east1-foursssolutions.cloudfunctions.net/email_outreach_send_v2";

const TONES = ["warm and professional", "concise and direct", "confident", "friendly", "formal"];
const TEMPLATE_CSV =
  "name,email,company,role\n" +
  "Jane Smith,jane@acme.com,Acme,Recruiter\n" +
  "John Doe,john@globex.com,Globex,Hiring Manager\n";

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "careersavvy-contacts-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function Outreach() {
  const userEmail = useSelector((s) => s.auth.auth.email);
  const apiToken = useSelector((s) => s.profile.apiToken);

  const post = useCallback(
    async (url, payload) => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiToken}` },
        body: JSON.stringify({ ...payload, email: userEmail }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.status === "error")) {
        throw new Error((data && data.message) || `Request failed (${res.status})`);
      }
      return data;
    },
    [apiToken, userEmail]
  );

  const [tab, setTab] = useState("campaigns");

  /* ---------- Gmail connection ---------- */
  const [conn, setConn] = useState(null);
  const [connLoading, setConnLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);

  /* ---------- lists ---------- */
  const [lists, setLists] = useState([]);
  const [listsLoading, setListsLoading] = useState(true);
  const [listsError, setListsError] = useState("");
  const [newListName, setNewListName] = useState("");
  const [creatingList, setCreatingList] = useState(false);
  const [showListForm, setShowListForm] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [listContacts, setListContacts] = useState([]);
  const [listDetailLoading, setListDetailLoading] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [uploadingContacts, setUploadingContacts] = useState(false);

  /* ---------- campaigns ---------- */
  const [campaigns, setCampaigns] = useState([]);
  const [campLoading, setCampLoading] = useState(true);
  const [campError, setCampError] = useState("");
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    prompt: "",
    tone: TONES[0],
    attach_resume: true,
    daily_cap: 20,
    send_interval_minutes: 30,
    list_id: "",
  });
  const [creating, setCreating] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [campDetailLoading, setCampDetailLoading] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  /* ---------- dialogs ---------- */
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // {type:'campaign'|'list', item}

  const oauthRanRef = useRef(false);
  const newListFileRef = useRef(null);
  const addFileRef = useRef(null);

  /* ---------- loaders ---------- */
  const loadStatus = useCallback(async () => {
    setConnLoading(true);
    try {
      setConn(await post(OAUTH_API, { action: "oauth_status" }));
    } catch (err) {
      setConn({ connected: false });
    } finally {
      setConnLoading(false);
    }
  }, [post]);

  const loadLists = useCallback(async () => {
    setListsLoading(true);
    setListsError("");
    try {
      const data = await post(API, { action: "list_lists" });
      setLists(Array.isArray(data.lists) ? data.lists : []);
    } catch (err) {
      setListsError(err.message || "Could not load lists");
      setLists([]);
    } finally {
      setListsLoading(false);
    }
  }, [post]);

  const loadCampaigns = useCallback(async () => {
    setCampLoading(true);
    setCampError("");
    try {
      const data = await post(API, { action: "list_campaigns" });
      setCampaigns(Array.isArray(data.campaigns) ? data.campaigns : []);
    } catch (err) {
      setCampError(err.message || "Could not load campaigns");
      setCampaigns([]);
    } finally {
      setCampLoading(false);
    }
  }, [post]);

  useEffect(() => {
    if (!userEmail || !apiToken) return;
    loadStatus();
    loadLists();
    loadCampaigns();
  }, [userEmail, apiToken, loadStatus, loadLists, loadCampaigns]);

  /* OAuth redirect return */
  useEffect(() => {
    if (!userEmail || !apiToken || oauthRanRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) return;
    oauthRanRef.current = true;
    (async () => {
      try {
        const data = await post(OAUTH_API, { action: "oauth_callback", code, state });
        toast.success(`Gmail connected${data.google_email ? ` — ${data.google_email}` : ""}`);
        loadStatus();
      } catch (err) {
        toast.error(err.message || "Could not connect Gmail");
      } finally {
        window.history.replaceState({}, "", window.location.pathname);
      }
    })();
  }, [userEmail, apiToken, post, loadStatus]);

  /* ---------- Gmail connect / disconnect ---------- */
  const connectGmail = async () => {
    setConnecting(true);
    try {
      const data = await post(OAUTH_API, {
        action: "oauth_start",
        redirect_uri: `${window.location.origin}/outreach`,
      });
      if (data.auth_url) window.location.assign(data.auth_url);
      else throw new Error("No authorization URL returned");
    } catch (err) {
      toast.error(err.message || "Could not start Gmail connection");
      setConnecting(false);
    }
  };

  const disconnectGmail = async () => {
    try {
      await post(OAUTH_API, { action: "oauth_disconnect" });
      toast.success("Gmail disconnected");
      loadStatus();
    } catch (err) {
      toast.error(err.message || "Could not disconnect");
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await post(SEND_API, { action: "send_test", tone: TONES[0], attach_resume: false });
      toast.success(`Test sent to ${conn?.google_email || "your inbox"} — check your Gmail`);
    } catch (err) {
      toast.error(err.message || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  /* ---------- lists ---------- */
  const createList = async (csv) => {
    if (!newListName.trim()) {
      toast.error("Give the list a name");
      return;
    }
    setCreatingList(true);
    try {
      const payload = { action: "create_list", name: newListName.trim() };
      if (typeof csv === "string" && csv.trim()) payload.csv_text = csv;
      const data = await post(API, payload);
      toast.success("List created");
      const createdName = newListName.trim();
      setNewListName("");
      setShowListForm(false);
      await loadLists();
      if (data.list_id) openList({ list_id: data.list_id, name: createdName });
    } catch (err) {
      toast.error(err.message || "Could not create list");
    } finally {
      setCreatingList(false);
    }
  };

  const handleNewListFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (newListFileRef.current) newListFileRef.current.value = "";
    if (!file) return;
    if (!newListName.trim()) {
      toast.error("Name the list first, then choose a CSV");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2 MB)");
      return;
    }
    try {
      await createList(await file.text());
    } catch (err) {
      toast.error("Could not read that file");
    }
  };

  const openList = async (l) => {
    setSelectedList(l);
    setListContacts([]);
    setCsvText("");
    setListDetailLoading(true);
    try {
      const data = await post(API, { action: "get_list", list_id: l.list_id });
      setListContacts(Array.isArray(data.contacts) ? data.contacts : []);
      if (data.list) setSelectedList(data.list);
    } catch (err) {
      toast.error(err.message || "Could not load list");
    } finally {
      setListDetailLoading(false);
    }
  };

  const addContacts = async (text) => {
    if (!selectedList) return;
    const payloadText = typeof text === "string" ? text : csvText;
    if (!payloadText.trim()) {
      toast.error("Add at least one contact");
      return;
    }
    setUploadingContacts(true);
    try {
      const data = await post(API, {
        action: "upload_contacts",
        list_id: selectedList.list_id,
        csv_text: payloadText,
      });
      toast.success(`${data.added} added${data.skipped ? `, ${data.skipped} skipped` : ""}`);
      setCsvText("");
      openList(selectedList);
      loadLists();
    } catch (err) {
      toast.error(err.message || "Could not add contacts");
    } finally {
      setUploadingContacts(false);
    }
  };

  /* Remove a single contact from the open list (Pavithra Outreach #1). */
  const removeContact = async (ct) => {
    try {
      await post(API, { action: "delete_contact", contact_id: ct.contact_id });
      setListContacts((cur) => cur.filter((c) => c.contact_id !== ct.contact_id));
      loadLists();
    } catch (err) {
      toast.error(err.message || "Could not remove contact");
    }
  };

  const handleAddFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (addFileRef.current) addFileRef.current.value = "";
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("File too large (max 2 MB)");
      return;
    }
    try {
      await addContacts(await file.text());
    } catch (err) {
      toast.error("Could not read that file");
    }
  };

  const deleteList = async (l) => {
    setConfirm(null);
    try {
      await post(API, { action: "delete_list", list_id: l.list_id });
      toast.success("List deleted");
      if (selectedList?.list_id === l.list_id) setSelectedList(null);
      loadLists();
    } catch (err) {
      toast.error(err.message || "Could not delete list");
    }
  };

  /* ---------- campaigns ---------- */
  const createCampaign = async () => {
    if (!form.name.trim()) {
      toast.error("Give your campaign a name");
      return;
    }
    if (!form.list_id) {
      toast.error("Pick a recipient list");
      return;
    }
    setCreating(true);
    try {
      await post(API, {
        action: "create_campaign",
        name: form.name.trim(),
        prompt: form.prompt.trim(),
        tone: form.tone,
        attach_resume: form.attach_resume,
        daily_cap: Number(form.daily_cap) || 20,
        send_interval_minutes: Number(form.send_interval_minutes) || 30,
        list_id: form.list_id,
      });
      toast.success("Campaign created");
      setForm({
        name: "",
        prompt: "",
        tone: TONES[0],
        attach_resume: true,
        daily_cap: 20,
        send_interval_minutes: 30,
        list_id: "",
      });
      setShowCampaignForm(false);
      loadCampaigns();
    } catch (err) {
      toast.error(err.message || "Could not create campaign");
    } finally {
      setCreating(false);
    }
  };

  const openCampaign = async (c) => {
    setSelectedCampaign(c);
    setRecipients([]);
    setCampDetailLoading(true);
    try {
      const data = await post(API, { action: "get_campaign", campaign_id: c.campaign_id });
      setRecipients(Array.isArray(data.recipients) ? data.recipients : []);
      if (data.campaign) setSelectedCampaign(data.campaign);
    } catch (err) {
      toast.error(err.message || "Could not load campaign");
    } finally {
      setCampDetailLoading(false);
    }
  };

  const toggleStatus = async (c, e) => {
    if (e) e.stopPropagation();
    const next = c.status === "active" ? "paused" : "active";
    /* don't let a campaign be activated with no Gmail connected — otherwise it
       shows "active" but every send silently no-ops (Pavithra #7). */
    if (next === "active" && !conn?.connected) {
      toast.error("Connect your Gmail first to activate this campaign");
      return;
    }
    try {
      await post(API, { action: "set_campaign_status", campaign_id: c.campaign_id, new_status: next });
      loadCampaigns();
      if (selectedCampaign?.campaign_id === c.campaign_id) setSelectedCampaign({ ...selectedCampaign, status: next });
    } catch (err) {
      toast.error(err.message || "Could not update status");
    }
  };

  const startSending = async (c) => {
    if (!conn?.connected) {
      toast.error("Connect your Gmail first");
      return;
    }
    if (!recipients.length) {
      toast.error("This list has no recipients");
      return;
    }
    try {
      await post(API, { action: "set_campaign_status", campaign_id: c.campaign_id, new_status: "active" });
      toast.success("Campaign activated — sending begins within a few minutes. Watch the sent count update, or use “Send now” on a recipient to send instantly.");
      setSelectedCampaign({ ...c, status: "active" });
      loadCampaigns();
    } catch (err) {
      toast.error(err.message || "Could not start campaign");
    }
  };

  const deleteCampaign = async (c) => {
    setConfirm(null);
    try {
      await post(API, { action: "delete_campaign", campaign_id: c.campaign_id });
      toast.success("Campaign deleted");
      if (selectedCampaign?.campaign_id === c.campaign_id) setSelectedCampaign(null);
      loadCampaigns();
    } catch (err) {
      toast.error(err.message || "Could not delete campaign");
    }
  };

  const previewRecipient = async (r) => {
    setPreview({ loading: true });
    setPreviewLoading(true);
    try {
      const data = await post(SEND_API, {
        action: "preview",
        campaign_id: selectedCampaign.campaign_id,
        contact_id: r.contact_id,
      });
      setPreview({ subject: data.subject, body: data.body, to: r.email });
    } catch (err) {
      toast.error(err.message || "Preview failed");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendNow = async (r) => {
    if (!conn?.connected) {
      toast.error("Connect your Gmail first");
      return;
    }
    setSendingId(r.contact_id);
    try {
      await post(SEND_API, {
        action: "send_now",
        campaign_id: selectedCampaign.campaign_id,
        contact_id: r.contact_id,
      });
      toast.success(`Sent to ${r.email}`);
      openCampaign(selectedCampaign);
    } catch (err) {
      toast.error(err.message || "Send failed");
    } finally {
      setSendingId(null);
    }
  };

  const statusTone = (s) => (s === "active" ? "accent" : s === "paused" ? "warn" : "plain");
  const sendTone = (s) => (s === "sent" ? "accent" : s === "failed" ? "danger" : "plain");

  /* ============================ render ============================ */
  return (
    <div className="stack-4 ob-wrap">
      {/* Page title + subtitle live in the top bar now (no duplicate h1 here). */}

      {/* ---------- Gmail connection ---------- */}
      <section className="ld-glass ob-conn">
        <span className="ob-conn-icon">
          <LuMail size={20} />
        </span>
        {connLoading ? (
          <div className="grow">
            <Skel h={18} w="40%" />
            <Skel h={13} w="60%" style={{ marginTop: 6 }} />
          </div>
        ) : conn?.connected ? (
          <>
            <div className="grow" style={{ minWidth: 0 }}>
              <div className="row" style={{ gap: 8 }}>
                <span className="t-sm t-ink" style={{ fontWeight: 600 }}>
                  {conn.google_email}
                </span>
                <Badge tone="accent">
                  <LuCheck size={12} /> Connected
                </Badge>
              </div>
              <div className="t-xs mt-1">Sent {conn.total_sends || 0} emails. Campaigns send from this inbox.</div>
            </div>
            <Btn variant="ghost" size="sm" className="btn-pill" onClick={sendTest} disabled={testing}>
              {testing ? <Spinner /> : <LuSend size={14} />} Send test to myself
            </Btn>
            <Btn variant="ghost" size="sm" className="btn-pill" onClick={disconnectGmail}>
              <LuUnlink size={14} /> Disconnect
            </Btn>
          </>
        ) : (
          <>
            <div className="grow">
              <div className="t-sm t-ink" style={{ fontWeight: 600 }}>
                No Gmail connected
              </div>
              <div className="t-xs mt-1">Connect a Gmail account to send outreach from (send permission only).</div>
            </div>
            <Btn size="sm" className="btn-pill btn-mint" onClick={connectGmail} disabled={connecting}>
              {connecting ? <Spinner /> : <LuLink size={14} />} Connect Gmail
            </Btn>
          </>
        )}
      </section>

      {/* ---------- sub-tabs ---------- */}
      <div className="ob-tabs">
        <button type="button" className={`ob-tab${tab === "campaigns" ? " on" : ""}`} onClick={() => setTab("campaigns")}>
          <LuSend size={15} /> Campaigns
        </button>
        <button type="button" className={`ob-tab${tab === "lists" ? " on" : ""}`} onClick={() => setTab("lists")}>
          <LuList size={15} /> Lists
        </button>
      </div>

      {tab === "lists" ? (
        <div className="ob-grid">
          {/* left: lists index */}
          <section className="ld-glass ob-tile">
            <div className="row mb-3" style={{ gap: 8 }}>
              <h2 className="t-h3 grow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LuUsers size={16} className="t-accent" /> Your lists
              </h2>
              <Btn size="sm" className="btn-pill btn-mint" onClick={() => setShowListForm(true)}>
                <LuPlus size={14} /> New list
              </Btn>
            </div>
            {listsLoading ? (
              <div className="stack-2">
                <Skel h={54} r={16} />
                <Skel h={54} r={16} />
              </div>
            ) : listsError ? (
              <Note tone="danger">
                <span className="grow">{listsError}</span>
                <button type="button" onClick={loadLists} style={{ fontWeight: 600, textDecoration: "underline" }}>
                  Retry
                </button>
              </Note>
            ) : lists.length === 0 ? (
              <div className="ld-empty">
                <span className="ld-empty-icon">
                  <LuUsers size={18} />
                </span>
                <div className="t-xs measure">No lists yet — click “New list” to upload your contacts.</div>
              </div>
            ) : (
              <div className="stack-2">
                {lists.map((l) => (
                  <button
                    key={l.list_id}
                    type="button"
                    className={`ob-campaign${selectedList?.list_id === l.list_id ? " on" : ""}`}
                    onClick={() => openList(l)}
                  >
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="t-sm t-ink" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {l.name}
                      </div>
                      <div className="t-xs mt-1">{l.contact_count || 0} contacts</div>
                    </div>
                    <span
                      className="ob-icon-act danger"
                      role="button"
                      tabIndex={0}
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirm({ type: "list", item: l });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          setConfirm({ type: "list", item: l });
                        }
                      }}
                    >
                      <LuTrash2 size={15} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* right: list detail */}
          <section className="ld-glass ob-tile ob-detail">
            {!selectedList ? (
              <div className="ld-empty" style={{ height: "100%" }}>
                <span className="ld-empty-icon">
                  <LuList size={18} />
                </span>
                <div className="t-sm t-ink" style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                  Select a list
                </div>
                <div className="t-xs measure">Open a list to view and add recipients.</div>
              </div>
            ) : (
              <>
                <div className="row mb-3" style={{ gap: 8 }}>
                  <h2 className="t-h3 grow" style={{ minWidth: 0 }}>
                    {selectedList.name}
                  </h2>
                  <button type="button" className="btn btn-quiet btn-sm" title="Refresh" onClick={() => openList(selectedList)}>
                    <LuRefreshCw size={15} />
                  </button>
                </div>

                <div className="ob-uploadrow mb-2">
                  <button
                    type="button"
                    className="btn btn-pill btn-mint btn-sm"
                    onClick={() => addFileRef.current && addFileRef.current.click()}
                    disabled={uploadingContacts}
                  >
                    {uploadingContacts ? <Spinner /> : <LuUpload size={14} />} Upload CSV
                  </button>
                  <button type="button" className="btn btn-ghost btn-pill btn-sm" onClick={downloadTemplate}>
                    <LuDownload size={14} /> Template
                  </button>
                  <input ref={addFileRef} type="file" accept=".csv,.txt" onChange={handleAddFile} style={{ display: "none" }} />
                </div>
                <Field label="…or paste — one per line (name, email, company, role)">
                  <textarea
                    className="input"
                    rows={2}
                    placeholder={"Jane Smith, jane@acme.com, Acme, Recruiter\njohn@globex.com"}
                    value={csvText}
                    onChange={(e) => setCsvText(e.target.value)}
                  />
                </Field>
                <button type="button" className="btn btn-ghost btn-pill btn-sm mb-3" onClick={() => addContacts()} disabled={uploadingContacts}>
                  {uploadingContacts ? <Spinner /> : <LuUpload size={14} />} Add pasted
                </button>

                {listDetailLoading ? (
                  <div className="stack-2">
                    <Skel h={44} r={14} />
                    <Skel h={44} r={14} />
                  </div>
                ) : listContacts.length === 0 ? (
                  <div className="ld-empty">
                    <span className="ld-empty-icon">
                      <LuUsers size={18} />
                    </span>
                    <div className="t-xs measure">No recipients yet — upload a CSV or paste above.</div>
                  </div>
                ) : (
                  <div className="ob-contacts">
                    {listContacts.map((ct) => (
                      <div key={ct.contact_id} className="ob-contact">
                        <div className="grow" style={{ minWidth: 0 }}>
                          <div className="t-sm t-ink" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ct.name || ct.email}
                          </div>
                          <div className="t-xs" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {ct.email}
                            {ct.company ? ` · ${ct.company}` : ""}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          onClick={() => removeContact(ct)}
                          aria-label={`Remove ${ct.email}`}
                          title="Remove contact"
                          style={{ flex: "0 0 auto" }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      ) : (
        <div className="ob-grid">
          {/* left: campaigns index */}
          <section className="ld-glass ob-tile">
            <div className="row mb-3" style={{ gap: 8 }}>
              <h2 className="t-h3 grow" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LuSend size={16} className="t-accent" /> Your campaigns
              </h2>
              <Btn size="sm" className="btn-pill btn-mint" onClick={() => setShowCampaignForm(true)}>
                <LuPlus size={14} /> New campaign
              </Btn>
            </div>
            {campLoading ? (
              <div className="stack-2">
                <Skel h={58} r={16} />
                <Skel h={58} r={16} />
              </div>
            ) : campError ? (
              <Note tone="danger">
                <span className="grow">{campError}</span>
                <button type="button" onClick={loadCampaigns} style={{ fontWeight: 600, textDecoration: "underline" }}>
                  Retry
                </button>
              </Note>
            ) : campaigns.length === 0 ? (
              <div className="ld-empty">
                <span className="ld-empty-icon">
                  <LuMail size={18} />
                </span>
                <div className="t-xs measure">No campaigns yet — click “New campaign”.</div>
              </div>
            ) : (
              <div className="stack-2">
                {campaigns.map((c) => (
                  <button
                    key={c.campaign_id}
                    type="button"
                    className={`ob-campaign${selectedCampaign?.campaign_id === c.campaign_id ? " on" : ""}`}
                    onClick={() => openCampaign(c)}
                  >
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="row" style={{ gap: 8 }}>
                        <span className="t-sm t-ink" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {c.name}
                        </span>
                        <Badge tone={statusTone(c.status)}>{c.status}</Badge>
                      </div>
                      <div className="t-xs mt-1">
                        {c.list_name ? `${c.list_name} · ` : ""}
                        {c.sent_contacts || 0}/{c.total_contacts || 0} sent
                      </div>
                    </div>
                    {c.list_id ? (
                      <span
                        className="ob-icon-act"
                        role="button"
                        tabIndex={0}
                        title={c.status === "active" ? "Pause" : "Activate"}
                        onClick={(e) => toggleStatus(c, e)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") toggleStatus(c, e);
                        }}
                      >
                        {c.status === "active" ? <LuPause size={15} /> : <LuPlay size={15} />}
                      </span>
                    ) : null}
                    <span
                      className="ob-icon-act danger"
                      role="button"
                      tabIndex={0}
                      title="Delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirm({ type: "campaign", item: c });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.stopPropagation();
                          setConfirm({ type: "campaign", item: c });
                        }
                      }}
                    >
                      <LuTrash2 size={15} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* right: campaign detail */}
          <section className="ld-glass ob-tile ob-detail">
            {!selectedCampaign ? (
              <div className="ld-empty" style={{ height: "100%" }}>
                <span className="ld-empty-icon">
                  <LuUsers size={18} />
                </span>
                <div className="t-sm t-ink" style={{ fontWeight: 600, fontFamily: "var(--font-display)" }}>
                  Select a campaign
                </div>
                <div className="t-xs measure">Open a campaign to see recipients and start sending.</div>
              </div>
            ) : (
              <>
                <div className="row mb-3" style={{ gap: 8 }}>
                  <h2 className="t-h3 grow" style={{ minWidth: 0 }}>
                    {selectedCampaign.name}
                  </h2>
                  <Badge tone={statusTone(selectedCampaign.status)}>{selectedCampaign.status}</Badge>
                  <button type="button" className="btn btn-quiet btn-sm" title="Refresh" onClick={() => openCampaign(selectedCampaign)}>
                    <LuRefreshCw size={15} />
                  </button>
                </div>

                <div className="ob-runbar mb-3">
                  <div className="t-xs grow">
                    {selectedCampaign.list_name ? <>List <b>{selectedCampaign.list_name}</b> · </> : null}
                    Max <b>{selectedCampaign.daily_cap ?? 20}</b>/day · <b>{selectedCampaign.send_interval_minutes ?? 30}</b> min apart
                    {selectedCampaign.attach_resume ? " · resume" : ""}
                  </div>
                  {selectedCampaign.status === "active" ? (
                    <Btn variant="ghost" size="sm" className="btn-pill" onClick={() => toggleStatus(selectedCampaign)}>
                      <LuPause size={14} /> Pause
                    </Btn>
                  ) : (
                    <Btn size="sm" className="btn-pill btn-mint" onClick={() => startSending(selectedCampaign)} disabled={!conn?.connected}>
                      <LuPlay size={14} /> Start sending
                    </Btn>
                  )}
                </div>

                {!conn?.connected ? (
                  <div className="mb-3">
                    <Note tone="amber">
                      <LuMail size={15} />
                      <span>No Gmail connected — connect one at the top of this page. Sending is disabled until then.</span>
                    </Note>
                  </div>
                ) : null}

                <div className="ld-eyebrow mb-1">Recipients</div>
                {campDetailLoading ? (
                  <div className="stack-2">
                    <Skel h={44} r={14} />
                    <Skel h={44} r={14} />
                  </div>
                ) : recipients.length === 0 ? (
                  <div className="ld-empty">
                    <span className="ld-empty-icon">
                      <LuUsers size={18} />
                    </span>
                    <div className="t-xs measure">
                      No recipients — this campaign&apos;s list is empty. Add contacts in the Lists tab.
                    </div>
                  </div>
                ) : (
                  <div className="ob-contacts">
                    {recipients.map((r) => (
                      <div key={r.contact_id} className="ob-contact">
                        <div className="grow" style={{ minWidth: 0 }}>
                          <div className="t-sm t-ink" style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {r.name || r.email}
                          </div>
                          <div className="t-xs" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {r.email}
                            {r.company ? ` · ${r.company}` : ""}
                          </div>
                        </div>
                        <Badge tone={sendTone(r.send_status)}>{r.send_status || "pending"}</Badge>
                        <button type="button" className="btn btn-quiet btn-sm" title="Preview" onClick={() => previewRecipient(r)}>
                          <LuEye size={15} />
                        </button>
                        <button
                          type="button"
                          className="btn btn-quiet btn-sm"
                          title="Send now"
                          onClick={() => sendNow(r)}
                          disabled={sendingId === r.contact_id || r.send_status === "sent" || !conn?.connected}
                        >
                          {sendingId === r.contact_id ? <Spinner /> : <LuSend size={15} />}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}

      {/* ---------- New list dialog ---------- */}
      <Dialog open={showListForm} onClose={() => setShowListForm(false)}>
        <div className="ob-preview">
          <h3 className="t-h3 row mb-3" style={{ gap: 8 }}>
            <LuPlus size={16} className="t-accent" /> New list
          </h3>
          <div className="stack-3">
            <Field label="List name">
              <input
                className="input"
                placeholder="e.g. Recruiters — Q3"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                autoFocus
              />
            </Field>
            <div className="ob-uploadrow">
              <button
                type="button"
                className="btn btn-pill btn-mint btn-sm"
                onClick={() => newListFileRef.current && newListFileRef.current.click()}
                disabled={creatingList}
              >
                {creatingList ? <Spinner /> : <LuUpload size={14} />} Upload CSV &amp; create
              </button>
              <button type="button" className="btn btn-ghost btn-pill btn-sm" onClick={downloadTemplate}>
                <LuDownload size={14} /> Download template
              </button>
              <input ref={newListFileRef} type="file" accept=".csv,.txt" onChange={handleNewListFile} style={{ display: "none" }} />
            </div>
            <div className="t-xs">
              CSV columns: <b>name, email, company, role</b> (only email is required). Download the template to be sure
              of the format.
            </div>
            <div className="row mt-1" style={{ gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="ghost" size="sm" className="btn-pill" onClick={() => setShowListForm(false)}>
                Cancel
              </Btn>
              <Btn size="sm" className="btn-pill" onClick={() => createList()} disabled={creatingList}>
                <LuPlus size={14} /> Create empty list
              </Btn>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ---------- New campaign dialog ---------- */}
      <Dialog open={showCampaignForm} onClose={() => setShowCampaignForm(false)}>
        <div className="ob-preview">
          <h3 className="t-h3 row mb-3" style={{ gap: 8 }}>
            <LuPlus size={16} className="t-accent" /> New campaign
          </h3>
          <div className="stack-3">
            <Field label="Campaign name">
              <input
                className="input"
                placeholder="e.g. Spring backend roles"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
              />
            </Field>
            <Field label="Recipient list">
              {lists.length === 0 ? (
                <div className="t-xs">
                  No lists yet — create one in the <b>Lists</b> tab first.
                </div>
              ) : (
                <select className="input" value={form.list_id} onChange={(e) => setForm({ ...form, list_id: e.target.value })}>
                  <option value="">Select a list…</option>
                  {lists.map((l) => (
                    <option key={l.list_id} value={l.list_id}>
                      {l.name} ({l.contact_count || 0})
                    </option>
                  ))}
                </select>
              )}
            </Field>
            <Field label="What should the email say?">
              <textarea
                className="input"
                rows={4}
                placeholder="Introduce myself as a backend engineer, mention interest in their team, ask for a quick chat."
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              />
            </Field>
            <Field label="Tone">
              <select className="input" value={form.tone} onChange={(e) => setForm({ ...form, tone: e.target.value })}>
                {TONES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid-2">
              <Field label="Max emails / day" hint="Hard-capped at 40">
                <input
                  className="input"
                  type="number"
                  min={1}
                  max={40}
                  value={form.daily_cap}
                  onChange={(e) => setForm({ ...form, daily_cap: e.target.value })}
                />
              </Field>
              <Field label="Minutes between emails" hint="Human-like spacing">
                <input
                  className="input"
                  type="number"
                  min={5}
                  max={1440}
                  value={form.send_interval_minutes}
                  onChange={(e) => setForm({ ...form, send_interval_minutes: e.target.value })}
                />
              </Field>
            </div>
            <label className="ob-check">
              <input
                type="checkbox"
                checked={form.attach_resume}
                onChange={(e) => setForm({ ...form, attach_resume: e.target.checked })}
              />
              <span className="row" style={{ gap: 6 }}>
                <LuPaperclip size={14} /> Attach my resume
              </span>
            </label>
            <div className="row mt-1" style={{ gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="ghost" size="sm" className="btn-pill" onClick={() => setShowCampaignForm(false)}>
                Cancel
              </Btn>
              <Btn size="sm" className="btn-pill btn-mint" onClick={createCampaign} disabled={creating}>
                {creating ? <Spinner /> : <LuPlus size={14} />} Create campaign
              </Btn>
            </div>
          </div>
        </div>
      </Dialog>

      {/* ---------- preview dialog ---------- */}
      <Dialog open={!!preview} onClose={() => setPreview(null)}>
        <div className="ob-preview">
          <h3 className="t-h3 row mb-2" style={{ gap: 8 }}>
            <LuEye size={16} className="t-accent" /> Email preview
          </h3>
          {previewLoading || preview?.loading ? (
            <div className="row" style={{ gap: 8 }}>
              <Spinner /> <span className="t-sm">Generating…</span>
            </div>
          ) : preview ? (
            <>
              <div className="t-xs mb-1">To: {preview.to}</div>
              <div className="t-sm t-ink mb-2" style={{ fontWeight: 600 }}>
                {preview.subject}
              </div>
              <pre className="ob-preview-body">{preview.body}</pre>
            </>
          ) : null}
          <div className="row mt-3" style={{ justifyContent: "flex-end" }}>
            <Btn variant="ghost" size="sm" className="btn-pill" onClick={() => setPreview(null)}>
              Close
            </Btn>
          </div>
        </div>
      </Dialog>

      {/* ---------- delete confirm dialog ---------- */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <div className="ob-preview">
          <h3 className="t-h3 mb-2">Delete {confirm?.type}?</h3>
          <p className="t-sm measure">
            “{confirm?.item?.name}”{confirm?.type === "list" ? " and its recipients" : ""} will be permanently removed.
            This cannot be undone.
          </p>
          <div className="row mt-3" style={{ gap: 8, justifyContent: "flex-end" }}>
            <Btn variant="ghost" size="sm" className="btn-pill" onClick={() => setConfirm(null)}>
              Cancel
            </Btn>
            <button
              type="button"
              className="btn btn-pill btn-sm ob-danger-btn"
              onClick={() => (confirm?.type === "list" ? deleteList(confirm.item) : deleteCampaign(confirm.item))}
            >
              <LuTrash2 size={14} /> Delete
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
